import React, { memo, useState, useEffect, useCallback, useMemo } from "react";
import { Table, Skeleton } from "antd";

import "./styles.scss";
import FilterInputSearch from "../filterInputSearch";
import FilterTabs from "../filterTabs";
import DateFilter from "../dateFilter";
import {
  all,
  assoc,
  equals,
  flatten,
  keys,
  map,
  pathOr,
  reduce,
  values,
} from "ramda";
import { getFilteredData } from "./helpers";

import { Empty } from "antd";
import { getValueByKey } from "../../utilities/helpers";
import TablePaginationCount from "../tablePaginationCount/TablePaginationCount";
import { MOBILE_WIDTH } from "../../constants/constants";
import useWindowDimensions from "../custom-hooks/useWindowDimensions";

const transformSearchValue = (value) => {
  if (value === "__empty__") return "";
  return value;
};

const AntdTable = React.forwardRef((props, ref) => {
  const {
    id,
    customBlankMsg,
    loading,
    columns,
    data = [],
    tableProps,
    hasTabFilter,
    searchPlaceholder,
    activeTab,
    filterFirstColumn = false,
    getSuggestions,
    applyFilters,
    getSuggestionsList,
    tabs,
    renderTabs,
    onTabChange,
    clearFilters,
    resetFilters,
    disableFilters = false,
    showBulkSelect = false,
    renderBulkAction,
    noDataText = "No Data",
    selectedTab,
    FiltersBar,
    applyFiltersBarFilters,
    practiceId,
    roleKey,
    clientSideFilter = false,
    scroll = {},
    showFilters = true,
    getDateFilter,
    downloadCSV,
    showDownload = true,
    startDate = null,
    endDate = null,
    range = "all",
    filename,
  } = props;
  const [selectionState, setSelectedState] = useState({
    selected: [],
    allSelected: false,
    unselected: [],
  });
  const pageNo = pathOr(1, ["pagination", "current"], tableProps);
  const { width } = useWindowDimensions();

  const count = pathOr(
    data.length,
    ["pagination", "filteredTotal"],
    tableProps
  );
  const removeSelectedItem = (id) => {
    setSelectedState((st) => {
      const selected = {
        ...st.selected,
        [pageNo]: (getValueByKey(st.selected, pageNo) || []).filter(
          (item) => item !== id
        ),
      };
      const onlyOneSelected = all((key) => {
        const page = parseInt(key);
        const contains =
          getValueByKey(st.selected, page)?.length ===
          (page === pageNo ? 1 : 0);
        return contains;
      }, Object.keys(st.selected));

      return {
        ...st,
        selected,
        allSelected: onlyOneSelected ? false : st.allSelected,
      };
    });
  };
  if (ref) {
    ref.current = {
      removeSelectedItem,
      searchPlaceholder,
      activeTab,
    };
  }

  const onActionPerformed = useCallback(
    () => {
      setSelectedState((st) => ({
        ...st,
        selected: [],
        allSelected: false,
        unselected: [],
      }));
    },
    [setSelectedState]
  );
  const filterList = useMemo(
    () =>
      columns
        ?.map((item, index) => {
          let values = [];
          if (!getSuggestionsList) {
            const filterListItems = data.map((tabItem) => {
              return getValueByKey(tabItem, item.key);
            });
            const formattedListItems = filterListItems.filter(
              (it) => it !== null && it !== ""
            );
            const uniqueFilterItems = [...new Set(formattedListItems)];
            values = uniqueFilterItems.map((it) => {
              return { key: it, name: it };
            });
          }
          if (
            item.key !== "actions" &&
            (activeTab === "groups" || filterFirstColumn || index !== 0) &&
            item.filter !== false
          ) {
            return {
              name: item.title,
              key: item.key,
              ...(getSuggestions
                ? {
                    values: [],
                    dataSource: async () => {
                      return await getSuggestions(item.key);
                    },
                  }
                : {
                    values,
                  }),
            };
          }
          return undefined;
        })
        .filter((item) => item !== undefined && item.name?.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, filterFirstColumn, getSuggestions, data]
  );

  const [state, setState] = useState({
    filteredTableData: data,
    tab: pathOr("all", [0, "value"], tabs),
    filterValues: {},
  });

  useEffect(
    () => {
      if (resetFilters !== undefined)
        setState((st) => ({
          ...st,
          resetFlag: !st.resetFlag,
          filterValues: {},
          encodedFilters: {},
        }));
      setSelectedState({ selected: [], unselected: [], allSelected: false });
    },
    [resetFilters]
  );
  const resetData = useCallback(
    () => {
      setState((st) => ({
        ...st,
        filteredTableData: data,
        filterValues: {},
        encodedFilters: {},
      }));
    },
    [data]
  );

  const handleFilter = async (values) => {
    const encodedFilterValues = reduce(
      (result, key) => {
        if (!clientSideFilter) {
          if (getValueByKey(values, key) !== undefined)
            return assoc(
              key,
              encodeURIComponent(
                transformSearchValue(getValueByKey(values, key))
              ),
              result
            );
          return result;
        } else {
          if (getValueByKey(values, key) !== undefined)
            return assoc(
              key,
              transformSearchValue(getValueByKey(values, key)),
              result
            );
          return result;
        }
      },
      {},
      keys(values)
    );
    const filterValues = reduce(
      (result, key) => {
        if (getValueByKey(values, key) !== undefined)
          return assoc(
            key,
            encodeURIComponent(
              transformSearchValue(getValueByKey(values, key))
            ),
            result
          );
        return result;
      },
      {},
      keys(values)
    );
    if (values && !equals(state.filterValues, values)) {
      setState((st) => ({
        ...st,
        filterValues: values,
        encodedFilters: filterValues,
      }));
      if (applyFilters) {
        applyFilters(encodedFilterValues);
        setSelectedState({
          selected: [],
          unselected: [],
          allSelected: false,
        });
        return;
      }
    }
    if (applyFilters) return;
    const filteredTableData = getFilteredData(data, state.tab);
    if (!values) {
      return setState((st) => ({
        ...st,
        filteredTableData: filteredTableData,
      }));
    }
    const filteredData =
      filteredTableData &&
      filteredTableData.filter((obj) => {
        const validKeys =
          values &&
          Object.keys(values).filter(
            (key) => getValueByKey(values, key) !== undefined
          );
        const filterBools =
          validKeys &&
          validKeys.map(
            (key) => getValueByKey(obj, key) === getValueByKey(values, key)
          );
        return filterBools && filterBools.every((item) => item === true);
      });
    if (values) {
      setState((st) => ({ ...st, filteredTableData: filteredData }));
    }
  };

  const handleTabChange = (e, dontUpdate = false) => {
    let tab = e.target ? e.target.value : e;
    if (onTabChange) {
      if (tab !== state.tab) {
        setState((st) => ({
          ...st,
          tab,
        }));

        onTabChange(tab, dontUpdate);
      }
      return;
    }
    let filteredData = data;
    if (tab === "main" || tab === "sub") {
      filteredData = getFilteredData(data, tab);
    } else {
      tab = "all";
    }
    const values = state.filterValues;
    if (!values) {
      return setState((st) => ({
        ...st,
        filteredTableData: filteredData,
        tab,
      }));
    }
    filteredData =
      filteredData &&
      filteredData.filter((obj) => {
        const validKeys =
          values &&
          Object.keys(values).filter(
            (key) => getValueByKey(values, key) !== undefined
          );
        const filterBools =
          validKeys &&
          validKeys.map(
            (key) => getValueByKey(obj, key) === getValueByKey(values, key)
          );
        return filterBools && filterBools.every((item) => item === true);
      });
    setState((st) => ({ ...st, tab, filteredTableData: filteredData }));
  };

  useEffect(
    () => {
      handleTabChange(
        {
          target: {
            value: pathOr("all", [0, "value"], tabs),
          },
        },
        true
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [activeTab]
  );

  useEffect(
    () => {
      handleTabChange(
        {
          target: {
            value: selectedTab,
          },
        },
        true
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [selectedTab]
  );

  useEffect(
    () => {
      setState((st) => ({ ...st, filteredTableData: data }));
      handleFilter(state.filterValues);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [data, state.filterValues]
  );

  let BlankMsgComponent;

  if (customBlankMsg) {
    const EmptyComponent = customBlankMsg.component;
    const blankMsgProps = customBlankMsg.props;
    BlankMsgComponent = <EmptyComponent {...blankMsgProps} />;
  }

  const locale = loading
    ? {
        locale: { emptyText: <Skeleton active paragraph={{ rows: 8 }} /> },
      }
    : !state.filteredTableData?.length
      ? {
          locale: {
            emptyText: BlankMsgComponent ? (
              BlankMsgComponent
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={noDataText}
              />
            ),
          },
        }
      : {};

  const selectedRows = flatten(values(selectionState.selected));
  const selectedCount = useMemo(
    () => {
      if (selectionState.allSelected) {
        return count - selectionState.unselected.length;
      }
      return selectedRows.length;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [count, selectionState]
  );

  useEffect(
    () => {
      if (selectionState.allSelected && data.length > 0 && !loading) {
        setSelectedState((st) => ({
          ...st,
          selected: getValueByKey(st.selected, pageNo)
            ? st.selected
            : {
                ...pathOr({}, ["selected"], st),
                [pageNo]: map(tableProps.rowKey, data),
              },
        }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [data]
  );

  useEffect(
    () => {
      if (props?.selectionsChanged) {
        props.selectionsChanged(selectionState);
      }
    },
    [selectionState]
  );

  return (
    <div className="table-container">
      {showFilters && (
        <div
          className={`table-filters-container ${!hasTabFilter &&
            "table-large-filters-container"}`}
        >
          <div className="table-filters-wrapper">
            <FilterInputSearch
              onActionPerformed={onActionPerformed}
              encodedFilters={state.filterValues || {}}
              filters={filterList}
              activeTab={activeTab}
              onFilterChange={handleFilter}
              placeholder={searchPlaceholder}
              handleResetData={resetData}
              getSuggestionsList={getSuggestionsList}
              disabled={loading || disableFilters}
              clearFilters={clearFilters}
              resetFlag={state.resetFlag}
              renderBulkAction={renderBulkAction}
              // selectedTab={state.tab}
              selectedRows={selectedRows}
              allSelected={selectionState.allSelected}
              showBulkSelect={showBulkSelect}
              selectedCount={
                clientSideFilter ? selectedRows.length : selectedCount
              }
              unselectedRows={
                selectionState.allSelected ? selectionState.unselected : []
              }
              count={pathOr(
                state.filteredTableData.length,
                ["pagination", "filteredTotal"],
                tableProps
              )}
              clientSideFilter={clientSideFilter}
            />
          </div>
          {hasTabFilter && selectedRows.length === 0 ? (
            renderTabs ? (
              <>{renderTabs()}</>
            ) : (
              <FilterTabs
                handleChange={handleTabChange}
                tab={state.tab}
                options={tabs}
              />
            )
          ) : null}
        </div>
      )}
      <DateFilter
        getDateFilter={getDateFilter}
        downloadCSV={downloadCSV}
        showDownload={showDownload}
        start={startDate}
        end={endDate}
        range={range}
        filename={filename}
        className="table-filter"
      />
      {FiltersBar && (
        <FiltersBar
          applyFiltersBarFilters={applyFiltersBarFilters}
          practiceId={practiceId}
          roleKey={roleKey}
          isDownloadDisabled={Boolean(!state.filteredTableData.length)}
        />
      )}

      <Table
        data-testid={id}
        showSorterTooltip={false}
        {...locale}
        columns={columns}
        pageSize={100}
        dataSource={state.filteredTableData}
        {...{
          ...tableProps,
          pagination: tableProps.pagination
            ? {
                showTotal: (total, range) => {
                  if (width > MOBILE_WIDTH) {
                    return `Showing ${range[0]} to ${
                      range[1]
                    } of ${total} results`;
                  } else {
                    return `${range[0]}-${range[1]}`;
                  }
                },
                responsive: true,
                pageSize: 15,
                onShowSizeChange: false,
                showSizeChanger: false,
                total: count,
                ...tableProps.pagination,
              }
            : undefined,
        }}
        scroll={{ x: scroll.x, y: scroll.y ? scroll.y : 380 }}
      />

      {!loading && <TablePaginationCount pagination={tableProps.pagination} />}
    </div>
  );
});

export default memo(
  AntdTable,
  ({ FiltersBar, ...prevProps }, { FiltersBar: Filters, ...nexProps }) => {
    return JSON.stringify(prevProps) === JSON.stringify(nexProps);
  }
);
